/* eslint-disable react/prop-types */

import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "@/firebase";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { sessionDuration } from "@/data/global_config";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let sessionTimeout = null;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);

      // Clear any previous session timer.
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
        sessionTimeout = null;
      }

      // ---------------------------------------------------------
      // NO FIREBASE USER
      // ---------------------------------------------------------
      if (!currentUser) {
        setUser(null);
        setIsAdmin(false);
        setIsEnrolled(false);

        localStorage.removeItem("user");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("isEnrolled");

        // IMPORTANT:
        // Do NOT remove orgCode or courseCode here.
        //
        // Login.jsx stores these values before Firebase login.
        // Removing them from this branch can create a race condition
        // during Google authentication.
        setLoading(false);

        return;
      }

      // ---------------------------------------------------------
      // FIREBASE USER EXISTS
      // ---------------------------------------------------------
      try {
        console.log(
          "AuthContext: Firebase user detected:",
          currentUser.email
        );

        // -------------------------------------------------------
        // Session timeout
        // -------------------------------------------------------
        try {
          const tokenResult = await currentUser.getIdTokenResult();

          if (tokenResult?.claims?.auth_time) {
            const authTime = tokenResult.claims.auth_time * 1000;

            const timeLeft =
              sessionDuration - (Date.now() - authTime);

            if (timeLeft > 0) {
              sessionTimeout = setTimeout(async () => {
                console.log("Session expired. Signing out.");

                try {
                  await signOut(auth);
                } catch (error) {
                  console.error(
                    "Session timeout sign-out failed:",
                    error
                  );
                }
              }, timeLeft);
            }
          } else {
            console.warn(
              "auth_time claim missing. Session timeout disabled."
            );
          }
        } catch (tokenError) {
          console.warn(
            "Unable to get Firebase ID token:",
            tokenError
          );
        }

        // -------------------------------------------------------
        // Set Firebase user immediately
        // -------------------------------------------------------
        setUser(currentUser);

        localStorage.setItem(
          "user",
          JSON.stringify({
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || "",
            photoURL: currentUser.photoURL || "",
          })
        );

        // -------------------------------------------------------
        // Read organization and course
        // -------------------------------------------------------
        const orgCode = localStorage.getItem("orgCode");
        const courseCode = localStorage.getItem("courseCode");

        console.log("AuthContext authorization values:", {
          email: currentUser.email,
          orgCode,
          courseCode,
        });

        if (!orgCode || !courseCode) {
          console.warn(
            "AuthContext: Missing orgCode or courseCode in localStorage."
          );

          setIsEnrolled(false);
          setIsAdmin(false);

          localStorage.setItem("isEnrolled", "false");
          localStorage.setItem("isAdmin", "false");

          setLoading(false);
          return;
        }

        // -------------------------------------------------------
        // Step 1: Validate organization
        // -------------------------------------------------------
        const orgRef = doc(
          db,
          `CorporateClients/${orgCode}`
        );

        const orgSnap = await getDoc(orgRef);

        if (!orgSnap.exists()) {
          console.warn(
            "AuthContext: Organization does not exist:",
            orgCode
          );

          setIsEnrolled(false);
          setIsAdmin(false);

          localStorage.setItem("isEnrolled", "false");
          localStorage.setItem("isAdmin", "false");

          setLoading(false);
          return;
        }

        const orgData = orgSnap.data();

        const enrolledCourses =
          orgData?.EnrolledCourses ?? [];

        const isCourseValid =
          enrolledCourses.includes(courseCode);

        console.log("AuthContext course validation:", {
          courseCode,
          enrolledCourses,
          isCourseValid,
        });

        // -------------------------------------------------------
        // Step 2: Find student
        // -------------------------------------------------------
        const studentInfoRef = collection(
          db,
          `CorporateClients/${orgCode}/studentInfo`
        );

        const studentSnap = await getDocs(studentInfoRef);

        let matched = false;
        let userIsAdmin = false;
        let matchedStudentId = null;

        studentSnap.forEach((studentDoc) => {
          const data = studentDoc.data();

          // Support both possible Firestore structures:
          //
          // 1. data.email
          // 2. data.attendance.email
          //
          // And both isAdmin locations.
          const studentEmail =
            data?.attendance?.email ??
            data?.email ??
            "";

          const studentAdmin =
            data?.attendance?.isAdmin ??
            data?.isAdmin ??
            false;

          if (
            currentUser.email &&
            studentEmail &&
            studentEmail.toLowerCase() ===
              currentUser.email.toLowerCase()
          ) {
            matched = true;
            userIsAdmin = studentAdmin === true;
            matchedStudentId = studentDoc.id;
          }
        });

        // -------------------------------------------------------
        // Save student ID
        // -------------------------------------------------------
        if (matchedStudentId) {
          localStorage.setItem(
            "studentId",
            matchedStudentId
          );
        }

        // -------------------------------------------------------
        // Final authorization
        // -------------------------------------------------------
        const authorized =
          matched && isCourseValid;

        setIsEnrolled(authorized);
        setIsAdmin(userIsAdmin);

        localStorage.setItem(
          "isEnrolled",
          String(authorized)
        );

        localStorage.setItem(
          "isAdmin",
          String(userIsAdmin)
        );

        console.log("AuthContext AUTH STATUS:", {
          email: currentUser.email,
          orgCode,
          courseCode,
          studentMatched: matched,
          courseValid: isCourseValid,
          studentId: matchedStudentId,
          isAdmin: userIsAdmin,
          authorized,
        });
      } catch (error) {
        console.error(
          "AuthContext authorization failed:",
          error
        );

        setIsEnrolled(false);
        setIsAdmin(false);

        localStorage.setItem(
          "isEnrolled",
          "false"
        );

        localStorage.setItem(
          "isAdmin",
          "false"
        );
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();

      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
      }
    };
  }, []);

  // -----------------------------------------------------------
  // Logout
  // -----------------------------------------------------------
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error);
    }

    setUser(null);
    setIsAdmin(false);
    setIsEnrolled(false);

    localStorage.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isEnrolled,
        loading,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};